/**
 * Utilitário para padronização de estilos de listagem no módulo de fossas sépticas
 * Este arquivo centraliza todos os estilos de listas, grids e cards de listagem
 */

/**
 * Estilo para container de lista
 */
export const listContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '15px'
};

/**
 * Estilo para item de lista (card)
 */
export const listItemStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '15px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  overflow: 'hidden'
};

/**
 * Estilo para card de obra (semelhante ao módulo de obras)
 */
export const obraCardStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef',
  marginBottom: '15px'
};

/**
 * Estilo para cabeçalho do card de obra
 */
export const obraCardHeaderStyle = {
  padding: '12px 15px',
  borderBottom: '1px solid #e9ecef',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

/**
 * Estilo para título do card de obra
 */
export const obraCardTitleStyle = {
  margin: 0,
  fontSize: '16px',
  fontWeight: '600',
  color: '#2e7d32' // Verde do módulo de fossas sépticas
};

/**
 * Estilo para status do card de obra
 */
export const obraCardStatusStyle = {
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '3px 8px',
  borderRadius: '4px',
  textTransform: 'uppercase'
};

/**
 * Estilo para corpo do card de obra
 */
export const obraCardBodyStyle = {
  padding: '15px'
};

/**
 * Estilo para linha de informação no card de obra
 */
export const obraCardInfoLineStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '8px',
  fontSize: '14px'
};

/**
 * Estilo para label de informação no card de obra
 */
export const obraCardLabelStyle = {
  color: '#666',
  fontWeight: 'normal'
};

/**
 * Estilo para valor de informação no card de obra
 */
export const obraCardValueStyle = {
  fontWeight: '500',
  textAlign: 'right'
};

/**
 * Estilo para barra de progresso no card de obra
 */
export const obraCardProgressContainerStyle = {
  marginTop: '10px',
  marginBottom: '5px'
};

/**
 * Estilo para barra de progresso externa no card de obra
 */
export const obraCardProgressOuterStyle = {
  height: '6px',
  backgroundColor: '#e9ecef',
  borderRadius: '3px',
  overflow: 'hidden'
};

/**
 * Estilo para barra de progresso interna no card de obra
 * @param {number} progress - Valor do progresso (0-100)
 * @returns {Object} - Estilo para a barra de progresso
 */
export const getObraCardProgressInnerStyle = (progress) => {
  return {
    height: '100%',
    width: `${progress}%`,
    backgroundColor: '#2e7d32', // Verde do módulo de fossas sépticas
    borderRadius: '3px'
  };
};

/**
 * Estilo para rodapé do card de obra
 */
export const obraCardFooterStyle = {
  padding: '10px 15px',
  borderTop: '1px solid #e9ecef',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px'
};

/**
 * Estilo para cabeçalho de item de lista
 */
export const listItemHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '10px'
};

/**
 * Estilo para título de item de lista
 */
export const listItemTitleStyle = {
  margin: 0,
  fontSize: '18px',
  fontWeight: '500'
};

/**
 * Estilo para badge de status
 * @param {string} color - Cor de fundo do badge
 * @returns {Object} - Estilo completo do badge
 */
export const getStatusBadgeStyle = (color) => {
  return {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: color
  };
};

/**
 * Estilo para grid de informações dentro do item de lista
 * @param {number} columns - Número de colunas
 * @returns {Object} - Estilo para o grid
 */
export const getInfoGridStyle = (columns = 2) => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '8px',
    fontSize: '14px',
    marginBottom: '15px'
  };
};

/**
 * Estilo para container de ações
 */
export const actionsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%'
};

/**
 * Estilo para container de observações
 */
export const observationsContainerStyle = {
  backgroundColor: '#f8f9fa',
  padding: '10px',
  borderRadius: '4px',
  marginTop: '5px',
  fontSize: '14px'
};

/**
 * Estilo para layout de três colunas em um item de lista
 */
export const threeColumnLayoutStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: '20px'
};

/**
 * Estilo para coluna principal (flex: 2)
 */
export const mainColumnStyle = {
  flex: 2
};

/**
 * Estilo para coluna secundária (flex: 2)
 */
export const secondaryColumnStyle = {
  flex: 2
};

/**
 * Estilo para coluna de ações (flex: 1)
 */
export const actionsColumnStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end'
};

/**
 * Estilo para indicador de carregamento
 */
export const loadingIndicatorStyle = {
  textAlign: 'center',
  padding: '40px'
};

/**
 * Estilo para spinner de carregamento
 */
export const spinnerStyle = {
  border: '4px solid rgba(0, 0, 0, 0.1)',
  borderLeftColor: '#2e7d32',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 20px'
};

/**
 * Estilo para mensagem de lista vazia
 */
export const emptyListStyle = {
  textAlign: 'center',
  padding: '40px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

/**
 * Estilo para ícone de lista vazia
 */
export const emptyListIconStyle = {
  color: '#2e7d32',
  marginBottom: '15px'
};

/**
 * Estilo para container de ações de lista vazia
 */
export const emptyListActionsStyle = {
  marginTop: '20px',
  display: 'flex',
  gap: '10px',
  justifyContent: 'center'
};

/**
 * Estilo para container de paginação
 */
export const paginationContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '20px'
};

/**
 * Estilo para botão de paginação
 * @param {boolean} isActive - Se o botão está ativo (página atual)
 * @returns {Object} - Estilo completo do botão de paginação
 */
export const getPaginationButtonStyle = (isActive, baseStyle) => {
  return {
    ...baseStyle,
    width: '32px',
    height: '32px',
    margin: '0 5px',
    padding: '0',
    justifyContent: 'center'
  };
};

/**
 * Estilo para container de filtros
 * @param {number} columns - Número de colunas
 * @returns {Object} - Estilo para o container de filtros
 */
export const getFiltersContainerStyle = (columns = 4) => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '15px',
    marginBottom: '20px'
  };
};

/**
 * Estilo para campo de busca com ícone
 */
export const searchFieldContainerStyle = {
  position: 'relative'
};

/**
 * Estilo para ícone de busca
 */
export const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#666'
};

/**
 * Estilo para input de busca
 */
export const getSearchInputStyle = (baseStyle) => {
  return {
    ...baseStyle,
    paddingLeft: '40px'
  };
};

/**
 * Estilo para cabeçalho de página
 */
export const pageHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px'
};

/**
 * Estilo para container de botões de ação no cabeçalho
 */
export const headerActionsStyle = {
  display: 'flex',
  gap: '10px'
};

/**
 * Estilo para badge informativo
 * @param {string} bgColor - Cor de fundo
 * @param {string} textColor - Cor do texto
 * @returns {Object} - Estilo completo do badge
 */
export const getInfoBadgeStyle = (bgColor, textColor) => {
  return {
    marginBottom: '15px',
    padding: '8px 12px',
    backgroundColor: bgColor,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: textColor
  };
};

export default {
  listContainerStyle,
  listItemStyle,
  listItemHeaderStyle,
  listItemTitleStyle,
  getStatusBadgeStyle,
  getInfoGridStyle,
  actionsContainerStyle,
  observationsContainerStyle,
  threeColumnLayoutStyle,
  mainColumnStyle,
  secondaryColumnStyle,
  actionsColumnStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  emptyListActionsStyle,
  paginationContainerStyle,
  getPaginationButtonStyle,
  getFiltersContainerStyle,
  searchFieldContainerStyle,
  searchIconStyle,
  getSearchInputStyle,
  pageHeaderStyle,
  headerActionsStyle,
  getInfoBadgeStyle
};